import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid2 as Grid,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { FC, useState } from "react";
import { GridColDef, GridRowParams, GridActionsCellItem, GridDeleteIcon } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { DbJobRuleInfos } from "../../../../Core/Api/Types/db-job-rule-infos";
import { JobTypes } from "../../../../Core/Api/Types/job-types";
import FormInputText from "../../../../Components/Forms/FormInputText/form-input-text";
import FormSection from "../../../../Components/Forms/FormSection/form-section";
import FormAutocomplete from "../../../../Components/Forms/FormAutocomplete/form-autocomplete";
import { produce } from "immer";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { DbStorageInfos } from "../../../../Core/Api/Types/db-storage-infos";
import { FormatSelector } from "../../../../Core/Api/Types/format-selector";
import { DbFormatInfos } from "../../../../Core/Api/Types/db-format-infos";
import FormInputNumber from "../../../../Components/Forms/FormInputNumber/form-input-number";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import InfoIcon from "@mui/icons-material/Info";
import JobDefEditSpecialParams from "./job-def-edit-special-params";
import SourceIcon from "@mui/icons-material/Source";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import { v4 as uuidv4 } from "uuid";
import { DeleteSection } from "../../../../Redux/Reducers/redux-slice-form-validation";
import { useDispatch } from "react-redux";
import { DbJobHighLevelDefinitionInfos } from "../../../../Core/Api/Types/db-job-high-level-definition-infos";
import FormSectionValidity from "../../../../Components/Forms/FormSectionNotValidWarningTrigger/form-section-validity";
import TableCellAlertSeverity from "../../../../Components/Table/TableCellAlertSeverity/table-cell-alert-severity";
import { useTranslation } from "react-i18next";

export type EditJobDefsProps = {
  formId: string;
  newJobRule: DbJobRuleInfos;
  setNewJobRule: React.Dispatch<React.SetStateAction<DbJobRuleInfos | null>>;
  storages: DbStorageInfos[];
  formats: DbFormatInfos[];
};

const EditJobDefs: FC<EditJobDefsProps> = (props) => {
  const { t } = useTranslation();
  const { newJobRule, setNewJobRule, formId, formats, storages } = props;

  const [selectedNewJobDefType, setSelectedNewJobDefType] = useState<JobTypes | null>(null);
  const [selectedJobDefRow, setSelectedJobDefRow] = useState<number | null>(null);

  const dispatch = useDispatch();

  const jobDefCols: GridColDef[] = [
    {
      field: "type",
      headerName: t("common:type"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (params) => (
        <FormSectionValidity
          formId={formId}
          sectionId={params?.row?.uuid}
          showWhenValid={<Chip label={params.value} sx={{ ml: 0.5 }} />}
          showWhenNotValid={<TableCellAlertSeverity color="error" data={params} />}
        />
      ),
    },
    {
      field: "name",
      headerName: t("common:name"),
      flex: CellSize.small,
      minWidth: CellSize.small,
    },
    {
      field: "priority",
      headerName: t("common:priority"),
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (params) => <Chip label={params.value} sx={{ ml: 0.5 }} />,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      flex: CellSize.actionsLarge,
      minWidth: CellSize.actionsLarge,
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        let actions: any[] = [];
        if ((params.id as number) > 0) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title={t("common:moveUp")}>
                  <ArrowUpwardIcon color="action" />
                </Tooltip>
              }
              label={t("common:moveUp")}
              onClick={() => {
                if (selectedJobDefRow !== null && selectedJobDefRow === (params.id as number))
                  setSelectedJobDefRow((x) => x! - 1);
                if (selectedJobDefRow !== null && selectedJobDefRow + 1 === (params.id as number))
                  setSelectedJobDefRow((x) => x! + 1);
                setNewJobRule(
                  produce((draft) => {
                    if (draft?.jobsDefinitions) {
                      const tempJob = { ...draft.jobsDefinitions[(params.id as number) - 1] };
                      draft.jobsDefinitions[(params.id as number) - 1] = {
                        ...draft.jobsDefinitions[params.id as number],
                      };
                      draft.jobsDefinitions[params.id as number] = tempJob;
                    }
                  })
                );
              }}
            />
          );
        }
        if (newJobRule && newJobRule.jobsDefinitions && (params.id as number) < newJobRule.jobsDefinitions.length - 1) {
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title={t("common:moveDown")}>
                  <ArrowDownwardIcon color="action" />
                </Tooltip>
              }
              label={t("common:moveDown")}
              onClick={() => {
                if (selectedJobDefRow !== null && selectedJobDefRow === (params.id as number))
                  setSelectedJobDefRow((x) => x! + 1);
                if (selectedJobDefRow !== null && selectedJobDefRow - 1 === (params.id as number))
                  setSelectedJobDefRow((x) => x! - 1);
                setNewJobRule(
                  produce((draft) => {
                    if (draft?.jobsDefinitions) {
                      const tempJob = { ...draft.jobsDefinitions[(params.id as number) + 1] };
                      draft.jobsDefinitions[(params.id as number) + 1] = {
                        ...draft.jobsDefinitions[params.id as number],
                      };
                      draft.jobsDefinitions[params.id as number] = tempJob;
                    }
                  })
                );
              }}
            />
          );
        }
        actions.push(
          <GridActionsCellItem
            icon={
              <Tooltip title={t("tooltip:title.copyJob")}>
                <ContentCopyIcon color="primary" />
              </Tooltip>
            }
            label={t("common:copy")}
            onClick={() => {
              setNewJobRule(
                produce((draft) => {
                  if (draft?.jobsDefinitions)
                    draft.jobsDefinitions.push({ ...draft.jobsDefinitions[params.id as number], uuid: uuidv4() });
                })
              );
              setSelectedJobDefRow(newJobRule!.jobsDefinitions!.length);
            }}
          />
        );
        actions.push(
          <GridActionsCellItem
            icon={
              <Tooltip title={t("common:removeJob")}>
                <GridDeleteIcon color="error" />
              </Tooltip>
            }
            label={t("common:remove")}
            onClick={() => {
              dispatch(DeleteSection({ formId: formId, sectionId: params?.row?.uuid }));
              setNewJobRule(
                produce((draft) => {
                  draft!.jobsDefinitions!.splice(params.id as number, 1);
                })
              );
              if (selectedJobDefRow && (params.id as number) < selectedJobDefRow) setSelectedJobDefRow((x) => x! - 1);
            }}
          />
        );
        actions.push(
          <GridActionsCellItem
            icon={
              <Tooltip title={t("common:configureJob")}>
                <MiscellaneousServicesIcon />
              </Tooltip>
            }
            label={t("common:configureJobLowerCase")}
            disabled={selectedJobDefRow === (params.id as number)}
            onClick={() => setSelectedJobDefRow(params.id as number)}
          />
        );
        return actions;
      },
    },
  ];

  const getSelectedJobType = (jobType: JobTypes) => {
    if (
      [
        JobTypes.TranscodeByTemplateAmberfin,
        JobTypes.AuthotSpeechToText,
        JobTypes.AudioWaveformGenerate,
        JobTypes.TranscodeUsingFFmpeg,
        JobTypes.ExecuteCommandAmberfin,
        JobTypes.ConvertSubtitle,
        JobTypes.AudioLanguageDetection,
        JobTypes.GenerateAAF,
        JobTypes.AudioAtmosRenderer,
      ].includes(jobType)
    ) {
      return `VDM.AirLab.Sdk.Core.Database.Types.Job.Job${selectedNewJobDefType}, VDM.AirLab.Sdk.Core`;
    }

    if (jobType === JobTypes.FileAsperaTransferServerToServerAmberfin) {
      return "VDM.AirLab.Sdk.Core.Database.Types.Job.JobAsperaTransferServerToServerAmberfin, VDM.AirLab.Sdk.Core";
    }

    if (jobType === JobTypes.FileAsperaTransferServerToServer) {
      return "VDM.AirLab.Sdk.Core.Database.Types.Job.JobAsperaTransferServerToServer, VDM.AirLab.Sdk.Core";
    }

    if (jobType === JobTypes.FileLiquidMetadataExport) {
      return "VDM.AirLab.Sdk.Core.Database.Types.Job.JobLiquidExport, VDM.AirLab.Sdk.Core";
    }

    if (jobType === JobTypes.FileCompress || jobType === JobTypes.FileUncompress) {
      return "VDM.AirLab.Sdk.Core.Database.Types.Job.JobFileCompress, VDM.AirLab.Sdk.Core";
    }

    if (jobType === JobTypes.QcVerifyFileBaton) {
      return "VDM.AirLab.Sdk.Core.Database.Types.Job.JobQcBatonVerifyFile, VDM.AirLab.Sdk.Core";
    }

    if (jobType === JobTypes.ConvertImage) {
      return "VDM.AirLab.Sdk.Core.Database.Types.Job.JobConvertImage, VDM.AirLab.Sdk.Core";
    }

    return "VDM.AirLab.Sdk.Core.Database.Types.JobBaseInfos, VDM.AirLab.Sdk.Core";
  };

  const handleAddJobDef = () => {
    setNewJobRule(
      produce((draft) => {
        if (!draft) return;
        if (!selectedNewJobDefType) return;
        const newJobDef: DbJobHighLevelDefinitionInfos = {
          uuid: uuidv4(),
          type: selectedNewJobDefType,
          name: selectedNewJobDefType as string,
          priority: 50,
          dst: { formatId: null, storageAutoSelectProvider: false, storageId: null },
          src: { fileMatched: null, formatIds: null, formatObjectType: null, formatSelector: null, storageIds: null },
          jobInfo: {
            $type: getSelectedJobType(selectedNewJobDefType),
            jobLongId: null,
            jobStringId: null,
            notification: null,
          },
        };

        draft.jobsDefinitions = draft.jobsDefinitions ? [...draft.jobsDefinitions, newJobDef] : [newJobDef];
      })
    );

    setSelectedJobDefRow(newJobRule.jobsDefinitions ? newJobRule.jobsDefinitions.length : 0);
    setSelectedNewJobDefType(null);
  };

  return (
    <FormSection
      size="small"
      sx={{ pb: 3 }}
      icon={<ElectricBoltIcon />}
      defaultExpanded={true}
      title={t("form:section.title.jobsDefinition")}
    >
      <Grid container spacing={2}>
        <Grid maxWidth={"100%"} size={{ sm: 12, md: 5.5 }}>
          <Box width={"100%"}>
            {newJobRule.jobsDefinitions && newJobRule.jobsDefinitions.length > 0 && (
              <DataTable
                noControls
                selectedRows={selectedJobDefRow !== null ? [selectedJobDefRow] : []}
                onRowClick={(params) => setSelectedJobDefRow(params?.row?.id as number)}
                rows={newJobRule.jobsDefinitions?.map((x, i) => ({ ...x, id: i })) || []}
                columns={jobDefCols.map((c) => ({ ...c, sortable: false }))}
                getRowHeight={() => "auto"}
                columnHeaderHeight={32}
                sx={{ pb: 1.5, height: "auto", width: "100%" }}
              />
            )}
          </Box>
          <Box display={"flex"} flexDirection={"row"}>
            <Autocomplete
              sx={{ width: "60%", mr: 2, mt: -3 }}
              renderInput={(params) => <TextField placeholder={t("field:placeholder.selectJobType")} {...params} />}
              value={selectedNewJobDefType}
              onChange={(e, v) => v && setSelectedNewJobDefType(v)}
              options={Object.values(JobTypes)}
              filterOptions={(x, s) => {
                let match = Object.values(JobTypes).filter((x) => x.toLowerCase().includes(s.inputValue.toLowerCase()));
                return x.filter((a) => a && match.includes(a));
              }}
            />
            <Button disabled={selectedNewJobDefType === null} onClick={handleAddJobDef}>
              {t("common:addJob")}
            </Button>
          </Box>
        </Grid>
        <>
          {selectedJobDefRow !== null &&
            newJobRule &&
            newJobRule.jobsDefinitions &&
            newJobRule.jobsDefinitions[selectedJobDefRow] && (
              <Grid size={{ sm: 12, md: 6.5 }}>
                <FormSection
                  size="small"
                  sx={{ mb: 2 }}
                  title={t("form:section.title.baseInfos")}
                  icon={<InfoIcon />}
                  defaultExpanded={true}
                >
                  <Grid spacing={2} container>
                    <Grid size={{ sm: 12, md: 6 }}>
                      <FormInputText
                        fullWidth
                        value={newJobRule.jobsDefinitions![selectedJobDefRow].name}
                        onChange={(v) =>
                          setNewJobRule(
                            produce((draft) => {
                              draft!.jobsDefinitions![selectedJobDefRow].name = v.target.value;
                            })
                          )
                        }
                        validationProps={{
                          formId: formId,
                          formSectionId: newJobRule.jobsDefinitions[selectedJobDefRow].uuid!,
                          fieldId: `jobDefName-${newJobRule.jobsDefinitions[selectedJobDefRow].uuid}`,
                          required: true,
                          initialValue: newJobRule.jobsDefinitions![selectedJobDefRow].name || undefined,
                        }}
                        stackProps={{ width: "100%" }}
                        label={t("common:name")}
                      />
                    </Grid>
                    <Grid size={{ sm: 12, md: 6 }}>
                      <FormInputNumber
                        fullWidth
                        stackProps={{ width: "100%" }}
                        value={newJobRule.jobsDefinitions[selectedJobDefRow].priority}
                        onChange={(v) =>
                          setNewJobRule(
                            produce((draft) => {
                              draft!.jobsDefinitions![selectedJobDefRow].priority = v.target.value
                                ? parseInt(v.target.value)
                                : null;
                            })
                          )
                        }
                        validationProps={{
                          formId: formId,
                          formSectionId: newJobRule.jobsDefinitions[selectedJobDefRow].uuid!,
                          fieldId: `jobDefPriority-${newJobRule.jobsDefinitions[selectedJobDefRow].uuid}`,
                          required: true,
                          initialValue: newJobRule.jobsDefinitions[selectedJobDefRow].priority ?? undefined,
                        }}
                        label={t("common:priority")}
                      />
                    </Grid>
                  </Grid>
                </FormSection>
                <FormSection
                  size="small"
                  sx={{ mb: 2 }}
                  title={t("form:section.title.sourceFileSetup")}
                  icon={<SourceIcon />}
                  defaultExpanded={true}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ sm: 12, md: 6 }}>
                      <FormAutocomplete
                        label={t("field:label.storages")}
                        value={newJobRule.jobsDefinitions[selectedJobDefRow].src?.storageIds ?? ([] as number[])}
                        multiple
                        fullWidth
                        options={storages.map((x) => x.id) as number[]}
                        getOptionLabel={(option: number | string) =>
                          storages.find((x) => x.id === option)?.name || "UNKNOWN"
                        }
                        isOptionEqualToValue={(option, value) => option === value}
                        filterOptions={(x, s) => {
                          let match = storages.filter((x) =>
                            x.name!.toLowerCase().includes(s.inputValue.toLowerCase())
                          );
                          return x.filter((a) => match.map((s) => s.id).includes(a));
                        }}
                        onChange={(v) =>
                          setNewJobRule(
                            produce((draft) => {
                              draft!.jobsDefinitions![selectedJobDefRow].src!.storageIds = v as number[];
                            })
                          )
                        }
                        validationProps={{
                          formId: formId,
                          formSectionId: newJobRule.jobsDefinitions[selectedJobDefRow].uuid!,
                          fieldId: `jobDefSrcStorages-${newJobRule.jobsDefinitions[selectedJobDefRow].uuid!}`,
                          required: true,
                          minLength: 1,
                          initialValue:
                            (newJobRule.jobsDefinitions[selectedJobDefRow].src?.storageIds as number[]) || [],
                        }}
                      />
                    </Grid>
                    <Grid size={{ sm: 12, md: 6 }}>
                      <FormAutocomplete
                        value={newJobRule.jobsDefinitions[selectedJobDefRow].src?.formatSelector ?? null}
                        options={Object.values(FormatSelector)}
                        label={t("field:label.formatSelectorMode")}
                        onChange={(v) =>
                          setNewJobRule(
                            produce((draft) => {
                              draft!.jobsDefinitions![selectedJobDefRow].src!.formatSelector = v;
                              draft!.jobsDefinitions![selectedJobDefRow].src!.formatObjectType = null;
                              draft!.jobsDefinitions![selectedJobDefRow].src!.formatIds = null;
                            })
                          )
                        }
                        validationProps={{
                          formId: formId,
                          formSectionId: newJobRule.jobsDefinitions[selectedJobDefRow].uuid!,
                          fieldId: `jobDefSrcFormatSelector-${newJobRule.jobsDefinitions[selectedJobDefRow].uuid!}`,
                          required: true,
                          initialValue: newJobRule.jobsDefinitions[selectedJobDefRow].src?.formatSelector ?? null,
                        }}
                      />
                    </Grid>
                    <Grid
                      sx={{
                        display:
                          newJobRule.jobsDefinitions[selectedJobDefRow].src!.formatSelector ===
                          FormatSelector.AnyMatchedByObjectType
                            ? "block"
                            : "none",
                      }}
                      size={{ sm: 12, md: 6 }}
                    >
                      <FormAutocomplete
                        label={t("field:label.objectTypeMatch")}
                        value={newJobRule.jobsDefinitions[selectedJobDefRow].src?.formatObjectType}
                        fullWidth
                        options={Object.values(ObjectType)}
                        onChange={(v) =>
                          setNewJobRule(
                            produce((draft) => {
                              draft!.jobsDefinitions![selectedJobDefRow].src!.formatObjectType = v;
                            })
                          )
                        }
                        filterOptions={(x, s) => {
                          let match = Object.values(ObjectType).filter((x) =>
                            x.toLowerCase().includes(s.inputValue.toLowerCase())
                          );
                          return x.filter((a) => match.includes(a));
                        }}
                        validationProps={{
                          formId: formId,
                          formSectionId: newJobRule.jobsDefinitions[selectedJobDefRow].uuid!,
                          fieldId: `jobDefSrcObjTypeMatch-${newJobRule.jobsDefinitions[selectedJobDefRow].uuid}`,
                          required:
                            newJobRule.jobsDefinitions[selectedJobDefRow].src!.formatSelector ===
                            FormatSelector.AnyMatchedByObjectType,
                          initialValue:
                            newJobRule.jobsDefinitions[selectedJobDefRow].src?.formatObjectType ?? undefined,
                        }}
                      />
                    </Grid>
                    <Grid
                      sx={{
                        display:
                          newJobRule.jobsDefinitions[selectedJobDefRow].src!.formatSelector === FormatSelector.Specified
                            ? "block"
                            : "none",
                      }}
                      size={{ sm: 12, md: 6 }}
                    >
                      <FormAutocomplete
                        label={t("field:label.formats")}
                        value={newJobRule.jobsDefinitions[selectedJobDefRow].src?.formatIds ?? ([] as number[])}
                        multiple
                        fullWidth
                        options={formats.map((x) => x.id) as number[]}
                        getOptionLabel={(option: number | string) =>
                          formats.find((x) => x.id === option)?.name || "UNKNOWN"
                        }
                        isOptionEqualToValue={(option, value) => option === value}
                        filterOptions={(x, s) => {
                          let match = formats.filter((x) => x.name!.toLowerCase().includes(s.inputValue.toLowerCase()));
                          return x.filter((a) => match.map((s) => s.id).includes(a));
                        }}
                        onChange={(v) =>
                          setNewJobRule(
                            produce((draft) => {
                              draft!.jobsDefinitions![selectedJobDefRow].src!.formatIds = v as number[];
                            })
                          )
                        }
                        validationProps={{
                          formId: formId,
                          formSectionId: newJobRule.jobsDefinitions[selectedJobDefRow].uuid!,
                          fieldId: `jobDefSrcformatIds-${newJobRule.jobsDefinitions[selectedJobDefRow].uuid!}`,
                          required:
                            newJobRule.jobsDefinitions[selectedJobDefRow].src!.formatSelector ===
                            FormatSelector.Specified,
                          minLength:
                            newJobRule.jobsDefinitions[selectedJobDefRow].src!.formatSelector ===
                            FormatSelector.Specified
                              ? 1
                              : 0,
                          initialValue:
                            (newJobRule.jobsDefinitions[selectedJobDefRow].src?.formatIds as number[]) || [],
                        }}
                      />
                    </Grid>
                  </Grid>
                </FormSection>
                <FormSection
                  size="small"
                  sx={{ mb: 2 }}
                  title={t("form:section.title.destinationFileSetup")}
                  icon={<MyLocationIcon />}
                  defaultExpanded={true}
                >
                  <Grid container spacing={2}>
                    <Grid size={{ sm: 12, md: 6 }}>
                      <FormAutocomplete
                        label={t("common:storage")}
                        value={newJobRule.jobsDefinitions![selectedJobDefRow].dst?.storageId || null}
                        fullWidth
                        options={storages.map((x) => x.id)}
                        onChange={(v) =>
                          setNewJobRule(
                            produce((draft) => {
                              draft!.jobsDefinitions![selectedJobDefRow].dst!.storageId = v;
                            })
                          )
                        }
                        getOptionLabel={(option: number | string) =>
                          storages.find((x) => x.id === option)?.name || "UNKNOWN"
                        }
                        isOptionEqualToValue={(option, value) => option === value}
                        filterOptions={(x, s) => {
                          let match = storages.filter((x) =>
                            x.name!.toLowerCase().includes(s.inputValue.toLowerCase())
                          );
                          return x.filter((a) => match.map((s) => s.id).includes(a || -1));
                        }}
                        validationProps={{
                          formId: formId,
                          formSectionId: newJobRule.jobsDefinitions[selectedJobDefRow].uuid!,
                          fieldId: `jobDefdstStorage-${newJobRule.jobsDefinitions![selectedJobDefRow].uuid}`,
                          required: !newJobRule!.jobsDefinitions![selectedJobDefRow].dst!.storageAutoSelectProvider,
                          initialValue: newJobRule.jobsDefinitions![selectedJobDefRow].dst?.storageId || undefined,
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              newJobRule!.jobsDefinitions![selectedJobDefRow].dst!.storageAutoSelectProvider || false
                            }
                            onChange={(e) =>
                              setNewJobRule(
                                produce((draft) => {
                                  draft!.jobsDefinitions![selectedJobDefRow].dst!.storageAutoSelectProvider =
                                    e.target.checked;
                                })
                              )
                            }
                          />
                        }
                        label={t("field:label.objectProviderStorageSwitch")}
                      />
                    </Grid>
                    {newJobRule.jobsDefinitions[selectedJobDefRow].type !== JobTypes.AudioAtmosRenderer && (
                      <Grid size={{ sm: 12, md: 6 }}>
                        <FormAutocomplete
                          label={t("field:label.formatInstruction")}
                          value={
                            formats.find((x) => x.id === newJobRule.jobsDefinitions![selectedJobDefRow].dst?.formatId)
                              ?.name ?? null
                          }
                          fullWidth
                          options={formats.map((x) => x.name)}
                          onChange={(v) =>
                            setNewJobRule(
                              produce((draft) => {
                                draft!.jobsDefinitions![selectedJobDefRow].dst!.formatId =
                                  formats.find((x) => x.name === v)?.id || null;
                              })
                            )
                          }
                          filterOptions={(x, s) => {
                            let match = formats.filter((x) =>
                              x.name!.toLowerCase().includes(s.inputValue.toLowerCase())
                            );
                            return x.filter((a) => match.map((s) => s.name).includes(a));
                          }}
                          validationProps={{
                            formId: formId,
                            formSectionId: newJobRule.jobsDefinitions[selectedJobDefRow].uuid!,
                            fieldId: `jobDefDstFormat-${newJobRule.jobsDefinitions[selectedJobDefRow].uuid}`,
                            required: false,
                            initialState: false,
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </FormSection>
                {!(
                  !newJobRule.jobsDefinitions ||
                  !newJobRule.jobsDefinitions[selectedJobDefRow] ||
                  !newJobRule.jobsDefinitions[selectedJobDefRow].type ||
                  ![
                    JobTypes.TranscodeByTemplateAmberfin,
                    JobTypes.AuthotSpeechToText,
                    JobTypes.AudioWaveformGenerate,
                    JobTypes.ArchiveDiva,
                    JobTypes.TranscodeUsingFFmpeg,
                    JobTypes.ExecuteCommandAmberfin,
                    JobTypes.FileAsperaTransferServerToServerAmberfin,
                    JobTypes.FileAsperaTransferServerToServer,
                    JobTypes.FileCopyAmberfin,
                    JobTypes.FileCopyInternalAsync,
                    JobTypes.ConvertSubtitle,
                    JobTypes.FileLiquidMetadataExport,
                    JobTypes.FileCompress,
                    JobTypes.FileUncompress,
                    JobTypes.AudioLanguageDetection,
                    JobTypes.QcVerifyFileBaton,
                    JobTypes.ConvertImage,
                    JobTypes.AudioAtmosRenderer,
                    JobTypes.WhisperSpeechToText,
                  ].includes(newJobRule.jobsDefinitions[selectedJobDefRow].type!)
                ) && (
                  <JobDefEditSpecialParams
                    formId={formId}
                    newJobRule={newJobRule}
                    selectedJobDefRow={selectedJobDefRow}
                    setNewJobRule={setNewJobRule}
                  />
                )}
              </Grid>
            )}
        </>
      </Grid>
    </FormSection>
  );
};

export default EditJobDefs;
